var _dec, _dec2, _class, _ColorPalette;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyrigfht notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Component } from 'react';
import { withStyle } from '@instructure/emotion';
import { addEventListener } from '@instructure/ui-dom-utils';
import { View } from '@instructure/ui-view';
import { px } from '@instructure/ui-utils';
import { withDeterministicId } from '@instructure/ui-react-utils';
import shallowCompare from "../utils/shallowCompare.js";
import { allowedProps } from "./props.js";
import generateStyle from "./styles.js";
import generateComponentTheme from "./theme.js";
/**
---
private: true
---
**/
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
let ColorPalette = (_dec = withDeterministicId(), _dec2 = withStyle(generateStyle, generateComponentTheme), _dec(_class = _dec2(_class = (_ColorPalette = class ColorPalette extends Component {
  constructor(props) {
    var _this$props$styles;
    super(props);
    this._id = void 0;
    this.ref = null;
    this._paletteRef = null;
    this._mouseMoveListener = void 0;
    this._mouseUpListener = void 0;
    this._paletteOffset = px((_this$props$styles = this.props.styles) === null || _this$props$styles === void 0 ? void 0 : _this$props$styles.paletteOffset);
    this.handleRef = el => {
      const elementRef = this.props.elementRef;
      this.ref = el;
      if (typeof elementRef === 'function') {
        elementRef(el);
      }
    };
    this.calcSaturation = position => Math.round(position / this.paletteWidth * 100) / 100;
    this.calcLuminance = position => Math.round((this.paletteHeight - position) / this.paletteHeight * 100) / 100;
    this.handleMouseUp = () => {
      this.removeEventListeners();
    };
    this.handleChange = e => {
      if (this.props.disabled) return;
      const clientX = e.clientX,
        clientY = e.clientY;
      const _this$calcColorPositi = this.calcColorPosition(clientX, clientY),
        newXPosition = _this$calcColorPositi.newXPosition,
        newYPosition = _this$calcColorPositi.newYPosition;
      this.setState({
        colorPosition: {
          x: newXPosition,
          y: newYPosition
        }
      });
      this.props.onChange({
        h: this.props.hue,
        s: this.calcSaturation(newXPosition),
        v: this.calcLuminance(newYPosition)
      });
    };
    this.state = {
      colorPosition: {
        x: 0,
        y: 0
      }
    };
    this._id = props.deterministicId('ColorMixer__Palette');
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props;
    (_this$props$makeStyle = (_this$props = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props, this.state);
    this.setState({
      colorPosition: this.calcPositionFromColor(this.props.color)
    });
  }
  componentDidUpdate(prevProps) {
    var _this$props$makeStyle2, _this$props2;
    (_this$props$makeStyle2 = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props2, this.state);
    if (shallowCompare(prevProps.color, this.props.color)) {
      this.setState({
        colorPosition: this.calcPositionFromColor(this.props.color)
      });
    }
  }
  componentWillUnmount() {
    this.removeEventListeners();
  }
  get paletteWidth() {
    return this._paletteRef.getBoundingClientRect().width - this._paletteOffset;
  }
  get paletteHeight() {
    return this._paletteRef.getBoundingClientRect().height - this._paletteOffset;
  }
  calcPositionFromColor(hsv) {
    const s = hsv.s,
      v = hsv.v;
    const x = s * this.paletteWidth;
    const y = (1 - v) * this.paletteHeight;
    return {
      x,
      y
    };
  }
  handlePaletteMouseDown(e) {
    // Prevent selection outside palette during dragging the indicator
    e.preventDefault();

    // Restore focus since preventDefault() blocks automatic focus on mouse event
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.focus();
    }
    this.handleChange(e);
    this._mouseMoveListener = addEventListener(window, 'mousemove', this.handleChange);
    this._mouseUpListener = addEventListener(window, 'mouseup', this.handleMouseUp);
  }
  removeEventListeners() {
    var _this$_mouseMoveListe, _this$_mouseUpListene;
    (_this$_mouseMoveListe = this._mouseMoveListener) === null || _this$_mouseMoveListe === void 0 ? void 0 : _this$_mouseMoveListe.remove();
    (_this$_mouseUpListene = this._mouseUpListener) === null || _this$_mouseUpListene === void 0 ? void 0 : _this$_mouseUpListene.remove();
  }
  calcColorPosition(clientX, clientY) {
    const _getBoundingClientRec = this._paletteRef.getBoundingClientRect(),
      x = _getBoundingClientRec.x,
      y = _getBoundingClientRec.y;
    return this.applyBoundaries(clientX - x, clientY - y);
  }
  applyBoundaries(x, y) {
    let newXPosition = x;
    let newYPosition = y;
    if (x > this.paletteWidth) {
      newXPosition = this.paletteWidth;
    }
    if (x < 0) {
      newXPosition = 0;
    }
    if (y > this.paletteHeight) {
      newYPosition = this.paletteHeight;
    }
    if (y < 0) {
      newYPosition = 0;
    }
    return {
      newXPosition,
      newYPosition
    };
  }
  handleKeyDown(e) {
    if (this.props.disabled) return;
    const key = e.key;
    if (key === 'Tab') return;
    e.preventDefault();
    let deltaX = 0;
    let deltaY = 0;
    if (key === 'ArrowLeft' || key === 'a') {
      deltaX = -2;
    }
    if (key === 'ArrowRight' || key === 'd') {
      deltaX = 2;
    }
    if (key === 'ArrowUp' || key === 'w') {
      deltaY = -2;
    }
    if (key === 'ArrowDown' || key === 's') {
      deltaY = 2;
    }
    const _this$applyBoundaries = this.applyBoundaries(this.state.colorPosition.x + deltaX, this.state.colorPosition.y + deltaY),
      newXPosition = _this$applyBoundaries.newXPosition,
      newYPosition = _this$applyBoundaries.newYPosition;
    this.setState({
      colorPosition: {
        x: newXPosition,
        y: newYPosition
      }
    });
    this.props.onChange({
      h: this.props.hue,
      s: this.calcSaturation(newXPosition),
      v: this.calcLuminance(newYPosition)
    });
  }
  render() {
    var _this$props$styles2, _this$props$styles3, _this$props$styles4;
    return _jsxs(View, {
      elementRef: this.handleRef,
      disabled: this.props.disabled,
      position: "relative",
      background: "transparent",
      display: "inline-block",
      borderRadius: "medium",
      borderWidth: "0",
      padding: "0",
      as: "div",
      tabIndex: this.props.disabled ? void 0 : 0,
      onKeyDown: e => this.handleKeyDown(e),
      onMouseDown: e => this.handlePaletteMouseDown(e),
      "aria-label": this.props.navigationExplanationScreenReaderLabel,
      id: this._id,
      role: "button",
      children: [_jsx("div", {
        css: (_this$props$styles2 = this.props.styles) === null || _this$props$styles2 === void 0 ? void 0 : _this$props$styles2.indicator
      }), this.props.disabled && _jsx("div", {
        css: (_this$props$styles3 = this.props.styles) === null || _this$props$styles3 === void 0 ? void 0 : _this$props$styles3.disabledOverlay
      }), _jsx("div", {
        css: (_this$props$styles4 = this.props.styles) === null || _this$props$styles4 === void 0 ? void 0 : _this$props$styles4.palette,
        ref: ref => {
          this._paletteRef = ref;
        }
      })]
    });
  }
}, _ColorPalette.displayName = "ColorPalette", _ColorPalette.allowedProps = allowedProps, _ColorPalette.componentId = 'ColorMixer.Palette', _ColorPalette)) || _class) || _class);
export default ColorPalette;